import { useNavigate, useParams } from 'react-router-dom';
import {
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useState,
} from 'react';
import classNames from 'classnames';
import { useAppDispatch, useAppSelector } from '@Hooks/index';
import { NotificationContext } from '@Context/NotificationContext';
import { Flex, Modal } from 'antd';
import { MButton, MTable } from '@Components/Base';
import { TenderPriceResponse } from '@Types/api/form/tenderRequest/TenderPriceResponse';
import { TenderPricePostRequest } from '@Types/api/form/tenderRequest/TenderPricePostRequest';
import { RcCustomRequestOptions } from '@Components/types/UploadType';
import { fileUploadActions } from '@Store/slices/fileUploadSlice';
import { generateFilepath } from '@Utils/generateFilepath';
import { RcFile } from 'antd/es/upload';
import { currentUserSelector } from '@Store/selectors/userInfoSelector';
import {
  useDeleteDocumentByIdMutation,
  useFormListMutation,
  useFormUploadMutation,
} from '@Store/api/dmsApiSlice';
import { DocumentFormUploadResponse } from '@Types/api/form';
import { Attachment } from '@Components/types/Attachment';
import { getDocument } from '@Features/utils/document';
import { FileUpload } from '@Types/index';
import { currentFileUploadSelector } from '@Store/selectors/fileUploadSelector';
import { SheetLayout } from '../components/SheetLayout';
import RenderHeader from '../components/SheetLayout/components/RenderHeader/RenderHeader';
import styles from './PreviousPricing.module.css';
import PricingSelectType from './components/PricingSelectType/PricingSelectType';
import AttachmentButton from './components/AttachmentButton/AttachmentButton';
import { mapColumns } from './list/MapColumns';
import {
  useGetTenderPriceQuery,
  usePostTenderPriceMutation,
} from '../../store/api/TenderRequestApiSlice';
import { pricingDataSourceMap } from './list/DataSource';

import {
  getDocumentFolder,
  getFormUploadFileMetaData,
} from './utils/getFormUploadFIleMetaData';
import { SelectedType } from './enums/SelectedType.enum';

const PreviousPricing: React.FC = () => {
  const { successNotification, errorNotification } =
    useContext(NotificationContext);
  const { tenderId } = useParams();
  const dispatch = useAppDispatch();
  const currentUser = useAppSelector(currentUserSelector);
  const currentFileUpload = useAppSelector(currentFileUploadSelector);
  const [formUpload] = useFormUploadMutation();
  const [deleteDocumentById] = useDeleteDocumentByIdMutation();
  const [getFormList] = useFormListMutation();
  const [uploadedFile, setUploadedFile] = useState<Attachment | undefined>(
    undefined
  );
  const [selectedPriceType, setSelectedPriceType] = useState<string>('');
  const [fileAttached, setFileAttached] = useState<boolean>(false);
  const { data, refetch } = useGetTenderPriceQuery(
    { tenderId: tenderId! },
    {
      skip: !tenderId,
    }
  );
  const [listData, setListData] = useState<Array<TenderPriceResponse>>([]);
  const [saveTenderPrice] = usePostTenderPriceMutation();
  const navigate = useNavigate();
  const onCancel = useCallback(() => {
    navigate('../');
  }, [navigate]);

  const [changedPricingItems, setChangedPricingItems] = useState<
    Array<TenderPricePostRequest & { id: number | string }>
  >([]);

  const handleSelect = (value: string): void => {
    setSelectedPriceType(value);
  };

  const clearFileData = (): void => {
    setUploadedFile(undefined);
    setFileAttached(false);
    dispatch(fileUploadActions.setFile(null));
  };

  const handleChangeDataItem = (changedDataItem: TenderPriceResponse): void => {
    setChangedPricingItems(
      changedPricingItems.map((item) => {
        if (item.id === changedDataItem.matCode) {
          return {
            ...item,
            filePath: currentFileUpload?.filepath,
            prevLPONo: changedDataItem.prevLPONo,
            rate: changedDataItem.prevRate,
          };
        }
        return item;
      })
    );
  };

  useLayoutEffect(() => {
    if (data) {
      const { prevLPONo } = data[0];
      setSelectedPriceType(prevLPONo ? SelectedType.LPO : SelectedType.LOI);

      const initialTableState = data.map((item) => ({
        id: item.matCode,
        tenderItemID: Number(item.tenderItemID),
        filePath: item.prevRateAttach || '',
        prevLPONo: item.prevLPONo,
        rate: item.prevRate,
        prevRateType: 0,
      }));
      setChangedPricingItems(initialTableState);
      setListData(data);
    }
  }, [data]);

  const dataSource = useMemo(() => {
    const nulledData = listData.map((item) => {
      if (selectedPriceType === SelectedType.LPO) {
        return {
          ...item,
          prevRate: 0,
        };
      }
      return {
        ...item,
        prevLPONo: '',
      };
    });

    return pricingDataSourceMap(
      nulledData,
      selectedPriceType,
      handleChangeDataItem
    );
  }, [listData, changedPricingItems, selectedPriceType]);

  useLayoutEffect(() => {
    if (Array.isArray(data) && data[0]?.prevRateAttach && tenderId) {
      const { prevRateAttach: filepath } = data[0];

      const documentFolderPath = getDocumentFolder(tenderId);

      getFormList(documentFolderPath)
        .unwrap()
        .then((response) => {
          const matchedDocument = getDocument(response, filepath);

          if (matchedDocument) {
            setUploadedFile({
              id: matchedDocument.documentId,
              name: filepath,
            });
            setFileAttached(true);
          }
        });
    }
  }, [data, tenderId]);

  const removeUploadedFile = useCallback(() => {
    if (uploadedFile) {
      deleteDocumentById(uploadedFile!.id).unwrap();
      successNotification('Document has been deleted!');
      clearFileData();
      setChangedPricingItems(
        changedPricingItems.map((item) => ({ ...item, filePath: '' }))
      );
    }
  }, [uploadedFile, changedPricingItems]);

  const uploadFile = useCallback(
    (reqFileId: string): Promise<DocumentFormUploadResponse> => {
      const { formData, filepath } = currentFileUpload;

      const body = getFormUploadFileMetaData(reqFileId, filepath);

      return formUpload({
        document: JSON.stringify(body),
        body: formData,
      }).unwrap();
    },
    [currentFileUpload]
  );

  const onCheckIsExistDocument = (currentFile: FileUpload): void => {
    if (tenderId) {
      const documentFolderPath = getDocumentFolder(tenderId);

      getFormList(documentFolderPath)
        .unwrap()
        .then(async (response) => {
          const matchedDocument = getDocument(response, currentFile.filepath);

          if (matchedDocument) {
            setUploadedFile({
              id: matchedDocument.documentId,
              name: currentFile.filepath,
            });
            setFileAttached(true);
          } else {
            await uploadFile(String(tenderId)).then((fileResponse) => {
              setUploadedFile({
                id: fileResponse.documentId,
                name: currentFileUpload?.filepath,
              });
            });
            setFileAttached(true);
          }
        });
    }
  };

  const handleUploadedFileRemove = useCallback(
    (value: boolean | undefined) => {
      if (Boolean(uploadedFile) && Boolean(value)) {
        removeUploadedFile();
      }
      setFileAttached(false);
      dispatch(fileUploadActions.setFile(null));
    },
    [uploadedFile]
  );

  const handleReplaceUploadedFile = (): void => {
    handleUploadedFileRemove(true);
    setUploadedFile(undefined);
  };

  const handleSaveTenderPrice = useCallback(() => {
    saveTenderPrice(changedPricingItems)
      .unwrap()
      .then(async () => {
        refetch();
        successNotification('Pricing was saved!');

        if (currentFileUpload?.filepath && !uploadedFile) {
          onCheckIsExistDocument(currentFileUpload);
        }

        if (
          uploadedFile?.name &&
          currentFileUpload?.filepath &&
          currentFileUpload?.filepath !== uploadedFile?.name
        ) {
          handleReplaceUploadedFile();
          await uploadFile(String(tenderId)).then((response) => {
            setUploadedFile({
              id: response.documentId,
              name: currentFileUpload?.filepath,
            });
          });
          setFileAttached(true);
        }
      })
      .catch(({ error }) => {
        errorNotification(error);
      });
  }, [changedPricingItems, refetch, currentFileUpload?.filepath]);

  const handleFileRequest = useCallback(
    (options: RcCustomRequestOptions): void => {
      const { file, onSuccess, onError } = options;
      const cb = (): void => {};
      const successCb = onSuccess || cb;
      const errorCb = onError || cb;
      const formData = new FormData();
      formData.append('file', file);

      if (file) {
        const filepath = generateFilepath(
          'TR_PRICING',
          (file as RcFile).name,
          currentUser?.empNumber
        );
        dispatch(
          fileUploadActions.setFile({
            uploadedFile: file as RcFile,
            formData,
            filepath,
          })
        );
        successCb('Ok');
        successNotification('The pricing document was attached!');
        setChangedPricingItems(
          changedPricingItems.map((item) => ({
            ...item,
            filePath: filepath,
          }))
        );
      } else {
        errorCb(new Error('file not uploaded'));
      }
    },
    [currentUser, changedPricingItems]
  );

  useEffect(() => {
    if (currentFileUpload?.filepath) {
      setFileAttached(true);
    }
  }, [currentFileUpload]);

  return (
    <Modal
      open
      className={styles.modal}
      width="100%"
      height="100%"
      closable={false}
      footer={false}
    >
      <SheetLayout
        header={
          <RenderHeader
            headerCaption="Previous Pricing"
            onCancel={onCancel}
            handleBack={onCancel}
          />
        }
      >
        <Flex className={styles.contentWrapper} vertical gap={24}>
          <Flex justify="end" gap={12}>
            <PricingSelectType
              selectedValue={selectedPriceType}
              handleSelect={handleSelect}
            />
            <AttachmentButton
              uploadedFile={uploadedFile}
              onFileRemove={handleUploadedFileRemove}
              onFileRequest={handleFileRequest}
            />
          </Flex>
          <div
            className={classNames(
              styles.contentTable,
              fileAttached && styles.tablePaddingFileuploaded
            )}
          >
            <MTable
              columns={mapColumns}
              dataSource={[...dataSource]}
              totalItems={dataSource?.length}
            />
          </div>
          <Flex justify="end" gap={10}>
            <MButton onClick={onCancel} type="secondary">
              Cancel
            </MButton>
            <MButton onClick={handleSaveTenderPrice} type="primary">
              Save
            </MButton>
          </Flex>
        </Flex>
      </SheetLayout>
    </Modal>
  );
};

export default PreviousPricing;
